/**
 * Course settings · "Emailuri": which email kinds have a Resend template
 * behind them, and the test send of each.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Mail, Send } from "lucide-react";
import { api } from "../../../lib/api";

export default function EmailSettings({ canMutate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="settings-tab-emailuri">
      <EmailTemplatesCard canMutate={canMutate} />
    </div>
  );
}

const KIND_META = {
  lead_interest: { title: "Lead interest", subtitle: "Trimis la submit broșură" },
  lead_contact_confirmation: { title: "Confirmare formular contact", subtitle: "Trimis la trimiterea formularului de contact, fără atașament" },
  registration_no_payment: { title: "Înscriere (legacy)", subtitle: "Folosit doar dacă noile template-uri nu sunt configurate" },
  registration_pending_group: { title: "Înscriere fără grupă", subtitle: "Trimis când enrollment-ul nu are grupă atribuită" },
  registration_group_confirmed: { title: "Înscriere cu grupă", subtitle: "Trimis când enrollment-ul are grupă atribuită" },
  payment_confirmed: { title: "Plată confirmată", subtitle: "Trimis după IPN paid" },
  payment_confirmed_no_prima: { title: "Plată confirmată · grupă fără card Prima", subtitle: "Trimis după plată, la grupele unde cardul Prima e oprit. Fără șablon setat, pleacă emailul din aplicație, fără fraza despre card" },
  registration_fped: { title: "Înscriere · Curs FPED", subtitle: "Trimis la înscrierea într-o grupă Curs FPED. Fără șablon setat, pleacă emailul din aplicație, fără acreditare" },
  payment_confirmed_fped: { title: "Plată confirmată · Curs FPED", subtitle: "Trimis după plată, la grupele Curs FPED. Fără șablon setat, pleacă emailul din aplicație, fără acreditare și card Prima" },
  ministry_data_request_fped: { title: "Date pentru contract și certificat · Curs FPED", subtitle: "Cererea de date de după plată, la grupele Curs FPED: pentru contract și certificat, nu pentru minister" },
  payment_reminder_fped: { title: "Reminder plată · Curs FPED", subtitle: "Reamintirea de plată la grupele Curs FPED, fără cardul Prima. Fără șablon setat, pleacă emailul din aplicație" },
  payment_failed: { title: "Plată eșuată", subtitle: "Trimis după IPN failed" },
  payment_cancelled: { title: "Plată anulată", subtitle: "Trimis după IPN cancelled" },
  payment_reminder: { title: "Reminder plată", subtitle: "Cron 1h + 24h" },
  admin_payment_link: { title: "Link plată din CRM", subtitle: "Trimis manual de admin" },
  admin_payment_link_no_prima: { title: "Link plată din CRM · grupă fără card Prima", subtitle: "Același link, la grupele Curs FPED și la cele unde cardul Prima e oprit. Fără șablon setat, pleacă emailul din aplicație, fără fraza despre card" },
  referral_progress: { title: "Recomandări · un coleg a plătit", subtitle: "Către cel care recomandă, la fiecare coleg înscris cu codul lui care își plătește locul. Fără șablon setat, pleacă emailul din aplicație" },
  referral_reached: { title: "Recomandări · numărul atins", subtitle: "Către cel care recomandă, la colegul care îi completează numărul de recomandări. Fără șablon setat, pleacă emailul din aplicație" },
  referral_code: { title: "Codul de recomandare", subtitle: "Emailul separat cu codul: pentru cei deja înscriși și la retrimitere, din fișa înscrierii. Fără șablon setat, pleacă emailul din aplicație" },
  limo_access: { title: "Date acces MyLogiscool", subtitle: "Trimis la înscrierea în Logiscool și la resetarea parolei" },
};

function EmailTemplatesCard({ canMutate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [meEmail, setMeEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, st] = await Promise.all([
        api.get("/auth/me").catch(() => ({ data: { email: "" } })),
        api.get("/admin/emails/templates-status"),
      ]);
      setMeEmail(me.data?.email || "");
      setData(st.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async (kind) => {
    const to = meEmail || prompt("Trimite emailul de test către (email):");
    if (!to) return;
    setSending(kind);
    try {
      const r = await api.post("/admin/emails/test-template", { template_kind: kind, to });
      toast.success(`[TEST] ${kind} → ${to} (via ${r.data.via})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setSending(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 lg:col-span-2" data-testid="settings-card-emails">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7]">
          <Mail size={16} /> Email Templates
        </div>
        <div className="mt-3 text-sm text-[#6B7280] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Se încarcă…
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 lg:col-span-2" data-testid="settings-card-emails">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7]">
          <Mail size={16} /> Email Templates (Resend)
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#6B7280]">Resend:</span>
          <span className={`rounded-full px-2 py-0.5 font-bold ${data.resend_configured ? "bg-[#519872]/15 text-[#1f6045]" : "bg-[#FDECEC] text-[#9b1f2c]"}`}>
            {data.resend_configured ? "configurat" : "lipsește API key"}
          </span>
        </div>
      </div>

      <div className="rounded-xl bg-[#F0E7F4]/40 border border-[#965EA7]/20 p-3 text-xs text-[#373A45] my-4 leading-relaxed">
        Toate template-urile sunt <strong>opționale</strong>. Când env var-ul este gol → emailul folosește HTML-ul vechi din cod (fallback).
        Setează în Render <code className="text-[#7A4986]">RESEND_TEMPLATE_*</code> pentru a activa Resend Templates per tip.
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E5E7EB]">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F6F6] text-[#6B7280] text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Template</th>
              <th className="text-left px-4 py-2 font-semibold">Env var</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-right px-4 py-2 font-semibold">Acțiune</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => {
              const meta = KIND_META[item.kind] || { title: item.kind, subtitle: "" };
              return (
                <tr key={item.kind} className="border-t border-[#E5E7EB]" data-testid={`email-row-${item.kind}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#373A45]">{meta.title}</div>
                    <div className="text-[10px] text-[#6B7280]">{meta.subtitle}</div>
                    <details className="mt-1">
                      <summary className="text-[10px] text-[#7A4986] cursor-pointer">variabile ({item.variables.length})</summary>
                      <div className="mt-1 text-[10px] font-mono text-[#6B7280] break-words">
                        {item.variables.join(", ")}
                      </div>
                    </details>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#6B7280]">{item.env_var}</td>
                  <td className="px-4 py-3">
                    {item.configured ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#519872]/15 text-[#1f6045] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                        <CheckCircle2 size={11} /> configured
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#EDEEF0] text-[#373A45] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                        fallback
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => sendTest(item.kind)}
                      disabled={!canMutate || sending !== null}
                      className="rounded-full bg-[#373A45] hover:bg-[#1F2128] text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40"
                      data-testid={`email-test-btn-${item.kind}`}
                    >
                      {sending === item.kind ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Trimite test
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-[#6B7280] italic">
        Emailul de test folosește date de exemplu („Ana Popescu”, „390 RON” etc.). Fără șablon, subiectul începe cu{" "}
        <code className="text-[#7A4986]">[TEST]</code>; cu șablon, subiectul e cel din șablon.
        Destinație implicită: <strong>{meEmail || "—"}</strong>.
      </div>
    </div>
  );
}
